import {
  calculateDestinyPath,
  calculateExpressionNumber,
  calculateHeartDesireNumber,
  calculatePersonalityNumber,
  calculatePinCodes,
  getColor,
  getKarmicDebtNumbers,
} from "../../../shared/numerology";

import type { KarmaBirthDate, KarmaNumerology } from "@/lib/karma/types";

function safeInt(value: unknown, fallback: number) {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? Math.trunc(n) : fallback;
}

function digitsOf(n: number): number[] {
  return String(Math.abs(n))
    .split("")
    .map((ch) => ch.charCodeAt(0) - 48)
    .filter((d) => d >= 0 && d <= 9);
}

function reduceToSingleDigit(n: number): number {
  let total = Math.abs(n);
  while (total >= 10) {
    total = digitsOf(total).reduce((acc, d) => acc + d, 0);
  }
  return total || 1;
}

export function parseBirthDate(date: KarmaBirthDate): KarmaBirthDate {
  return {
    day: Math.min(31, Math.max(1, safeInt(date.day, 1))),
    month: Math.min(12, Math.max(1, safeInt(date.month, 1))),
    year: Math.min(2100, Math.max(1900, safeInt(date.year, 2000))),
  };
}

export function computeKarmaNumerology(
  displayName: string,
  birthDate: KarmaBirthDate,
): KarmaNumerology {
  const parsed = parseBirthDate(birthDate);
  const destinyPath = calculateDestinyPath(parsed.day, parsed.month, parsed.year);

  const parts = destinyPath.split(">");
  const last = parts.length ? parts[parts.length - 1] : "";

  const destinyNumber = reduceToSingleDigit(Number(last) || 0);
  const destinyColorName = getColor(destinyNumber);

  const masterCandidates = parts
    .map((p) => Number(p))
    .filter((n) => n === 11 || n === 22 || n === 33) as Array<11 | 22 | 33>;
  const lifePathMaster = masterCandidates.length ? masterCandidates[0] : undefined;

  const pin = calculatePinCodes(parsed.day, parsed.month, parsed.year);
  const pinText = pin.join("");

  const [firstName, ...rest] = displayName.trim().split(/\s+/g);
  const lastName = rest.join(" ");

  const expressionNumber = calculateExpressionNumber(firstName || displayName, lastName);
  const heartDesireNumber = calculateHeartDesireNumber(firstName || displayName, lastName);
  const personalityNumber = calculatePersonalityNumber(firstName || displayName, lastName);
  const birthDayNumber = reduceToSingleDigit(parsed.day);

  const karmicCodes = getKarmicDebtNumbers(firstName || displayName, lastName);

  return {
    destinyPath,
    destinyNumber,
    lifePathMaster,
    destinyColorName,
    expressionNumber,
    heartDesireNumber,
    personalityNumber,
    birthDayNumber,
    birthYear: parsed.year,
    pin,
    pinText,
    karmicCodes,
  };
}

export const ELEMENTS = {
  air: [1, 5],
  water: [2, 7],
  fire: [3, 6],
  earth: [4, 8],
  balance: [9],
} as const;

export type ElementKey = keyof typeof ELEMENTS;

export function elementOfDigit(digit: number): ElementKey {
  if (ELEMENTS.air.includes(digit as never)) return "air";
  if (ELEMENTS.water.includes(digit as never)) return "water";
  if (ELEMENTS.fire.includes(digit as never)) return "fire";
  if (ELEMENTS.earth.includes(digit as never)) return "earth";
  return "balance";
}

export function countElements(pin: number[]) {
  const counts: Record<ElementKey, number> = {
    air: 0,
    water: 0,
    fire: 0,
    earth: 0,
    balance: 0,
  };

  for (const d of pin) {
    if (!Number.isFinite(d)) continue;
    const digit = reduceToSingleDigit(d);
    counts[elementOfDigit(digit)] += 1;
  }

  return counts;
}

export function computeSynergy(pinA: number[], pinB: number[]): number {
  const len = Math.min(pinA.length, pinB.length);
  let total = 0;
  for (let i = 0; i < len; i += 1) {
    const a = Number.isFinite(pinA[i]) ? pinA[i] : 0;
    const b = Number.isFinite(pinB[i]) ? pinB[i] : 0;
    total += reduceToSingleDigit(a + b);
  }
  return reduceToSingleDigit(total);
}

export function sharedDigitCount(pinA: number[], pinB: number[]) {
  const a = new Set(pinA.map((d) => reduceToSingleDigit(d)));
  const b = new Set(pinB.map((d) => reduceToSingleDigit(d)));
  let shared = 0;
  for (const d of a) {
    if (b.has(d)) shared += 1;
  }
  return shared;
}

export function clamp01(n: number) {
  return Math.max(0, Math.min(1, n));
}
